/* eslint-disable arrow-body-style */
import CurrencyInput from '@gp/components/CurrencyInput';
import NumberInput from '@gp/components/NumberInput';
import PureSelect from '@gp/components/PureSelect';
import { TEAM_NAMES } from '@gp/constants/const';
import { PeriodFlag, getPeriodFlagName } from '@gp/enums/common';
import { KEY_MILESTONES } from '@gp/pages/Pipeline/ProjectDetails/Milestone/constants';
import {
  addMockIdBatch,
  generateDateComparator,
  generateMockId,
  getRoundIntegerNum,
  getSortedByDate,
  mathRound,
} from '@gp/utils/common';
import { getMilestoneLatestedName } from '@gp/utils/milestone';
import moment from 'moment';
import React from 'react';

import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';
import type { IMilestoneFinancialForecastVO, IMilestoneFinancialVO } from '@gp/types/milestones';
import type { TableColumn } from 'tea-component';

export const getIsKeyMilestones = (milestoneName?: string) =>
  !!milestoneName && KEY_MILESTONES.includes(milestoneName);

export const getMilestonePeriodFlag = (
  milestoneDate?: string,
  currentMilestoneDate?: string,
): PeriodFlag | '' => {
  if (!milestoneDate || !currentMilestoneDate) return '';
  const milestoneDateM = moment(milestoneDate);
  const currentMilestoneDateM = moment(currentMilestoneDate);
  if (!milestoneDateM.isValid() || !currentMilestoneDateM.isValid()) return '';
  return milestoneDateM.isSameOrBefore(currentMilestoneDateM)
    ? PeriodFlag.History
    : PeriodFlag.Future;
};

export const buildRecord = (): IMilestoneFinancialVO => ({
  mock_id: generateMockId(),
  // milestone id
  milestone_id: '',
  // milestone归属的milestone id
  parent_milestone_id: '',
  milestone: '',
  // milestone日期
  start_date: '',
  // dev cost的类型，历史的（Actual），未来的（Budget）
  cost_type: '',
  // dev cost的数值（单位为M）
  dev_cost: '',
  // hc cost的类型，历史的（Actual），未来的（Forecast）
  hc_type: '',
  // hc cost的数值（单位为M）
  dev_hc: '',
});

/**
 * 获取平铺、去重的milestones集合
 * 可能会出现milestone重名的场景，包含以下几个
 * 1、父级milestone重名，取时间最早那个
 * 2、一个是milestone、一个是sub milestone，取milestone
 * 3、同一父级milestone下的子级milestone重名，取时间最早那个
 * 4、不同父级milestone下的 子级milestone重名，取时间最早那个
 *
 * 以上注释为旧的逻辑，最新逻辑已删除子级milestone，代码中已删除子级milestone的处理逻辑，上面的注释保留是为了记录最早的逻辑介绍
 * @param milestones project的milestones集合
 * @returns 平铺、去重的milestones集合
 */
export const getSeparatedAndUniqueMilestones = (milestones?: IMilestone[]): IMilestone[] => {
  if (!Array.isArray(milestones) || !milestones.length) return [];
  const result: IMilestone[] = [];
  /**
   * 存储milestone的map
   * key为milestone名称
   * value为milestone的index和data，其中data保存milestone，index为milestone在result数组中的索引
   */
  const map: Record<string, { index: number; data: IMilestone }> = {};

  // 第一层循环遍历父级milestones，父级milestone的parent_milestone_id都不存在
  for (const milestone of milestones) {
    const { milestone: milestoneName, date: milestoneDate } = milestone || {};
    const milestoneNameNew = getMilestoneLatestedName(milestoneName);
    const milestoneNew = { ...milestone, milestone: milestoneNameNew };
    // 获取当前遍历的milestone名称，在map中匹配的cached存储数据
    const { index: storedMilestoneIndex = -1, data: storedMilestone } = map[milestoneNameNew] || {};
    // 不存在重名，直接push
    if (storedMilestoneIndex === -1) {
      result.push({ ...milestoneNew });
      // 同时在map中添加当前遍历的milestone的信息，以及当前遍历的milestone在result中的索引
      map[milestoneNameNew] = { index: result.length - 1, data: milestoneNew };
    } else {
      // 存在重名了
      const { parent_milestone_id: storedMilestoneIdOfParent, date: storedMilestoneDate } =
        storedMilestone || {};
      const milestoneDateM = milestoneDate ? moment(milestoneDate) : null;
      const storedMilestoneDateM = storedMilestoneDate ? moment(storedMilestoneDate) : null;
      // 1、父级milestone重名，取时间最晚那个
      if (!storedMilestoneIdOfParent) {
        if (milestoneDateM?.isValid() && milestoneDateM.isAfter(storedMilestoneDateM)) {
          /**
           * 当前遍历的milestone，在result中已存在同名的milestone了，并且当前遍历的milestone的date比result中存在那个同名的milestone的日期还要早
           * 1、通过map中存储的storedMilestoneIndex，用当前遍历的milestone直接替换storedMilestoneIndex索引位置保存的milestone
           * 2、同时更新map中存储的milestoneName对应的数据
           */
          result[storedMilestoneIndex] = { ...milestoneNew };
          map[milestoneNameNew].data = milestoneNew;
        }
      } else if (!!storedMilestoneIdOfParent) {
        // 2、一个是milestone、一个是sub milestone，取milestone
        // 当前遍历的milestone是父级milestone，storedMilestone是子级milestone，所以需要重置为当前遍历的milestone
        result[storedMilestoneIndex] = { ...milestoneNew };
        map[milestoneNameNew].data = milestoneNew;
      }
    }
  }

  return getSortedByDate(result, 'date');
};

export const getKeyMilestoneFinancialPeriodFlagInfo = (
  milestoneDate?: string,
  currentMilestoneDate?: string,
): { cost_type: PeriodFlag | ''; hc_type: PeriodFlag | '' } => {
  const milestonePeriodFlag = getMilestonePeriodFlag(milestoneDate, currentMilestoneDate);
  return { cost_type: milestonePeriodFlag || '', hc_type: milestonePeriodFlag || '' };
};

/**
 * 获取key milestones financials的表格初始化数据
 * @param keyMilestonesFinancials key milestones financials的表格数据（已编辑过，保存到服务端，再次编辑时直接从服务端拉回来的数据）
 * @param currentMilestoneDate 当前project的current milestone date
 * @param milestones 当前project的milestones集合（经过getSeparatedAndUniqueMilestones方法处理后的）
 * @param isEdit 当前是否为编辑状态
 * @returns key milestones financials的表格初始化数据
 */
export const getInitialKeyMilestonesFinancials = (
  keyMilestonesFinancials?: IMilestoneFinancialVO[],
  currentMilestoneDate?: string,
  milestones?: IMilestone[],
  isEdit?: boolean,
): IMilestoneFinancialVO[] => {
  if (Array.isArray(keyMilestonesFinancials) && keyMilestonesFinancials.length > 0) {
    if (!isEdit) return addMockIdBatch(keyMilestonesFinancials);
    const keyMilestonesFinancialsNew = keyMilestonesFinancials.map((keyMilestoneFinancial) => {
      const milestone = milestones?.find(
        (milestone) => milestone?.milestone === keyMilestoneFinancial?.milestone,
      );
      const { date: milestoneDate } = milestone || {};
      const periodFlagInfo = getKeyMilestoneFinancialPeriodFlagInfo(
        milestoneDate,
        currentMilestoneDate,
      );
      return {
        ...keyMilestoneFinancial,
        ...periodFlagInfo,
        mock_id: generateMockId(),
        start_date: milestoneDate || '',
      };
    });
    const dateComparator = generateDateComparator('start_date');
    return keyMilestonesFinancialsNew.sort((aMilestone, bMilestone) => {
      // @ts-ignore
      const { milestone: aMilestoneName } = aMilestone || {};
      const { milestone: bMilestoneName } = bMilestone || {};
      const aMilestoneNameIndex = KEY_MILESTONES.indexOf(aMilestoneName);
      const bMilestoneNameIndex = KEY_MILESTONES.indexOf(bMilestoneName);
      if (aMilestoneNameIndex === -1 && bMilestoneNameIndex === -1) {
        // 都是非固定的key milestones，按照日期升序排
        const sortNum = dateComparator(aMilestone, bMilestone);
        // 如果两个milestone的date一样，或者两个milestone都无date，则按照milestone名称字符排
        return sortNum !== 0 ? sortNum : aMilestoneName?.localeCompare(bMilestoneName);
      }
      // 都是固定的key milestones 按照固定的key milestones顺序排
      if (aMilestoneNameIndex !== -1 && bMilestoneNameIndex !== -1)
        return aMilestoneNameIndex - bMilestoneNameIndex;
      // 否则一个固定的key milestone，另一个非固定的key milestone，则按照固定的key milestone在前的规则排
      return aMilestoneNameIndex === -1 ? 1 : -1;
    });
  }

  return KEY_MILESTONES.map<IMilestoneFinancialVO>((name) => {
    const milestone = milestones?.find((milestone) => milestone?.milestone === name);
    const {
      milestone_id: milestoneId,
      parent_milestone_id: milestoneIdOfParent,
      date: milestoneDate,
    } = milestone || {};
    const periodFlagInfo = getKeyMilestoneFinancialPeriodFlagInfo(
      milestoneDate,
      currentMilestoneDate,
    );

    return {
      ...buildRecord(),
      ...periodFlagInfo,
      milestone_id: milestoneId || '',
      parent_milestone_id: milestoneIdOfParent || '',
      milestone: name,
      start_date: milestoneDate || '',
    };
  });
};

export const getInitialKeyMilestonesFinancialsForecast = (
  keyMilestonesFinancialsForecast?: IMilestoneFinancialForecastVO[] | null,
): IMilestoneFinancialForecastVO[] => {
  if (Array.isArray(keyMilestonesFinancialsForecast) && keyMilestonesFinancialsForecast.length > 0)
    return keyMilestonesFinancialsForecast.map((item) => ({ ...item, mock_id: generateMockId() }));
  return ['studio', 'publisher', 'fbi'].map((key) => {
    return {
      mock_id: generateMockId(),
      team: key,
      net_profit_ratio: '',
      y1_sales: '',
      y1_copies: '',
      y3_sales: '',
      y3_copies: '',
    };
  });
};

const formatDevCost = (devCost?: string): string => {
  const devCostTmp = mathRound(devCost, 2, '');
  return devCostTmp === '' ? '-' : `${devCostTmp}M`;
};

export const getColumns = (props: {
  isEdit: boolean;
  milestones?: IMilestone[];
  onChange?: (cvm: IMilestoneFinancialVO, key: string, value: string) => void;
  onMilestoneChange?: (cvm: IMilestoneFinancialVO, value: string) => void;
}): TableColumn<IMilestoneFinancialVO>[] => {
  const { isEdit, milestones, onChange, onMilestoneChange } = props;
  const milestonesOptions = milestones?.map((milestone) => {
    const { milestone: milestoneName, milestone_id: milestoneId } = milestone || {};
    return { text: milestoneName, value: milestoneId };
  });

  return [
    {
      key: 'milestone_id',
      header: 'By Milestone',
      align: 'left',
      render: (cvm) => {
        const { milestone: milestoneName, milestone_id: milestoneId } = cvm;
        if (!isEdit || getIsKeyMilestones(milestoneName)) return milestoneName;

        return (
          <PureSelect
            placeholder="Select existing Milestone"
            button={milestoneName}
            options={milestonesOptions}
            value={milestoneId}
            onChange={(payload) => {
              onMilestoneChange?.(cvm, payload);
            }}
          />
        );
      },
    },
    {
      key: 'dev_cost',
      header: 'Development Cost ($)',
      align: 'left',
      width: isEdit ? 242 : 'auto',
      render: (cvm) => {
        const { cost_type: devCostType, dev_cost: devCost } = cvm;
        const periodFlagName = getPeriodFlagName(devCostType);

        return (
          <div className="dbg-key-milestones-financials-table__currency-wrapper">
            {isEdit || (
              <span className="dbg-key-milestones-financials-table__num-value">
                {formatDevCost(devCost)}
              </span>
            )}
            {isEdit && (
              <CurrencyInput
                supportedDecimals
                decimalsLength={6}
                value={devCost}
                onChange={(payload) => {
                  onChange?.(cvm, 'dev_cost', payload);
                }}
              />
            )}
            {(isEdit || !!devCost) && (
              <span className="dbg-key-milestones-financials-table__period-name">
                {periodFlagName || '-'}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'dev_hc',
      header: 'Development Headcount',
      align: 'left',
      width: isEdit ? 242 : 'auto',
      render: (cvm) => {
        const { hc_type: devHcType, dev_hc: devHc } = cvm;
        const periodFlagName = getPeriodFlagName(devHcType, { futureName: 'Forecast' });

        return (
          <div className="dbg-key-milestones-financials-table__currency-wrapper">
            {isEdit || (
              <span className="dbg-key-milestones-financials-table__int-num-value">
                {getRoundIntegerNum(devHc)}
              </span>
            )}
            {isEdit && (
              <NumberInput
                supportedDecimals
                decimalsLength={4}
                value={devHc}
                onChange={(payload) => {
                  onChange?.(cvm, 'dev_hc', payload);
                }}
              />
            )}
            {(isEdit || !!devHc) && (
              <span className="dbg-key-milestones-financials-table__period-name">
                {periodFlagName || '-'}
              </span>
            )}
          </div>
        );
      },
    },
  ];
};

const FINANCIALS_FORECAST_CONTEXT_COLUMNS = [
  {
    key: 'y1_sales',
    reviewHeader: 'Y1 Revenue Forecast ($)',
    editHeader: (
      <>
        Y1 Revenue
        <br />
        Forecast ($)
      </>
    ),
  },
  {
    key: 'y1_copies',
    reviewHeader: 'Y1 Copy Sales Forecast',
    editHeader: (
      <>
        Y1 Copy Sales
        <br />
        Forecast
      </>
    ),
  },
  {
    key: 'y3_sales',
    reviewHeader: 'Y3 Revenue Forecast ($)',
    editHeader: (
      <>
        Y3 Revenue
        <br />
        Forecast ($)
      </>
    ),
  },
  {
    key: 'y3_copies',
    reviewHeader: 'Y3 Copy Sales Forecast',
    editHeader: (
      <>
        Y3 Copy Sales
        <br />
        Forecast
      </>
    ),
  },
];
export const getForecastColumns = (props: {
  isEdit: boolean;
  onChange?: (cvm: IMilestoneFinancialForecastVO, key: string, value: string) => void;
}): TableColumn<IMilestoneFinancialForecastVO>[] => {
  const { isEdit, onChange } = props;

  const contextColumns = FINANCIALS_FORECAST_CONTEXT_COLUMNS.map((context) => {
    const {
      key: contextKey,
      editHeader: contextEditHeader,
      reviewHeader: contextReviewHeader,
    } = context || {};
    return {
      key: contextKey,
      header: isEdit ? contextEditHeader : contextReviewHeader,
      align: 'left' as TableColumn['align'],
      width: isEdit ? 150 : 'auto',
      render: (cvm: IMilestoneFinancialForecastVO) => {
        const contextValue = cvm?.[contextKey as keyof IMilestoneFinancialForecastVO];

        return (
          <div className="dbg-key-milestones-financials-table__currency-wrapper dbg-key-milestones-financials-forecast-table__currency-wrapper">
            {isEdit || (
              <span className="dbg-key-milestones-financials-table__num-value">
                {formatDevCost(contextValue)}
              </span>
            )}
            {isEdit && (
              <CurrencyInput
                supportedDecimals
                decimalsLength={6}
                value={contextValue}
                onChange={(payload) => {
                  onChange?.(cvm, contextKey, payload);
                }}
              />
            )}
          </div>
        );
      },
    };
  });

  return [
    {
      key: 'team',
      header: 'Team',
      align: 'left',
      width: 104,
      render: (cvm) => {
        const { team } = cvm || {};
        return TEAM_NAMES[team] || team || '-';
      },
    },
    {
      key: 'net_profit_forecast',
      header: isEdit ? (
        <>
          Net Profit
          <br />
          Ratio
        </>
      ) : (
        <>Net Profit Ratio</>
      ),
      align: 'left',
      width: isEdit ? 140 : 'auto',
      render: (cvm) => {
        const { net_profit_ratio: netProfitRatio } = cvm || {};
        return (
          <div className="dbg-key-milestones-financials-table__currency-wrapper dbg-key-milestones-financials-forecast-table__currency-wrapper">
            {isEdit || (
              <span className="dbg-key-milestones-financials-table__int-num-value">
                {mathRound(netProfitRatio)}
              </span>
            )}
            {isEdit && (
              <NumberInput
                supportedDecimals
                decimalsLength={6}
                value={netProfitRatio}
                onChange={(payload) => {
                  onChange?.(cvm, 'net_profit_ratio', payload);
                }}
              />
            )}
          </div>
        );
      },
    },
    ...contextColumns,
  ];
};
